import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { Button } from 'antd';
import { useWindowSize } from 'react-use';

type BottomInfo = {
  title?: string;
  desc?: string;
};

type BottomSearchSectionProps = {
  bottomInfo?: BottomInfo;
  inputIcon?: React.ReactNode;
  placeholder?: string;
  buttonTitle?: string;
  loading?: boolean;
  searchVal?: string;
  inputVisible?: boolean;
  onValueChanged?: (value?: string) => void;
  onSearchClick?: (value?: string) => void;
};

const BottomSearchSection: React.FC<BottomSearchSectionProps> = ({
  bottomInfo,
  inputIcon,
  placeholder,
  buttonTitle,
  loading,
  searchVal,
  inputVisible,
  onValueChanged,
  onSearchClick,
}) => {
  const { t } = useTranslation('common');
  const [searchStr, setSearchStr] = useState(searchVal);
  const { width } = useWindowSize();

  useEffect(() => {
    if (searchVal !== searchStr) {
      setSearchStr(searchVal);
    }
  }, [searchVal]);

  const renderInput = () => {
    return (
      <div className={styles.inputContainer}>
        {inputIcon ? (
          inputIcon
        ) : (
          <Image src="/images/home/instagram-icon.webp" alt="instagram" width={24} height={24} />
        )}

        <input
          disabled={Boolean(loading)}
          value={searchVal}
          onChange={(event) => {
            const value = event.target.value;
            setSearchStr(value);
            onValueChanged && onValueChanged(value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              const value = (event.target as HTMLInputElement).value;
              onSearchClick && onSearchClick(value);
            }
          }}
          placeholder={placeholder ?? t('insert link or username')}
        />
        <Button
          loading={Boolean(loading)}
          className={styles.searchBtn}
          disabled={Boolean(loading)}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            onSearchClick && onSearchClick(searchStr);
          }}
        >
          {buttonTitle ?? t('Search Now')}
        </Button>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={styles.content}>
        <div className={styles.mainContent}>
          <div className={styles.title}>{bottomInfo?.title}</div>
          <p className={styles.desc}>{bottomInfo?.desc}</p>
          {renderInput()}
        </div>
      </div>
    );
  };

  const renderBottomSection = useMemo(() => {
    if (!bottomInfo) {
      return null;
    }

    return width > 768 ? renderContent() : inputVisible ? null : renderInput();
  }, [bottomInfo, width, loading, searchVal, inputVisible, searchStr]);

  return renderBottomSection;
};

export default BottomSearchSection;
